import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enIN } from 'date-fns/locale';

import { queryClient } from './queryClient';
import { theme } from '@/theme/theme';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';

/**
 * Every application-wide provider, in dependency order.
 *
 * AuthProvider sits INSIDE QueryClientProvider because it clears the query cache
 * on sign-out — the previous user's data must not survive into the next
 * session on a shared machine.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme} defaultMode="system">
        <CssBaseline enableColorScheme />

        {/* en-IN: dates render dd/MM/yyyy, which is what an Indian sales team
            reads and types. The default US locale would show 03/04 as 4 March
            in one place and 3 April in another. */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              autoHideDuration={4000}
            >
              <AuthProvider>{children}</AuthProvider>
            </SnackbarProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
